import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchAdminCompanyNetwork } from '@/api/(admin)/companies';

interface Args {
  companyId: string;
  isRemoved: boolean;
  /** 1-based, as the table counts pages. */
  page: number;
  limit: number;
}

/**
 * The companies linked to this one.
 *
 * The endpoint takes a **0-based** `page` and calls the size `count` — the
 * same convention as the roster, converted here since the table still counts
 * pages from 1. `removed` is a query flag on the same endpoint rather than a
 * separate resource, same as the roster and the detail endpoint.
 *
 * No sort and no filters: the old tab declared its columns sortable but wired
 * every `onSort` to an empty function, so nothing was ever sent.
 */
/** Shared by the tab's route loader and the hook. */
export function adminCompanyNetworkQueryOptions({
  companyId,
  isRemoved,
  page,
  limit,
}: Args) {
  const qs = new URLSearchParams({
    page: String(Math.max(0, page - 1)),
    count: String(limit),
  }).toString();

  return queryOptions({
    queryKey: [
      'admin',
      'companies',
      'network',
      companyId,
      isRemoved,
      page,
      limit,
    ] as const,
    queryFn: () => fetchAdminCompanyNetwork(companyId, qs, isRemoved),
    enabled: Boolean(companyId),
    staleTime: 60_000,
  });
}

export function useAdminCompanyNetwork(args: Args) {
  const query = useQuery(adminCompanyNetworkQueryOptions(args));

  return {
    ...query,
    rows: query.data?.data ?? [],
    totalCount: query.data?.metadata?.pagination?.total ?? 0,
  };
}
